import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  color: #4a5568;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 1px #4299e1;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 1px #4299e1;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #4a5568;
  cursor: pointer;
`;

const Button = styled.button`
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: #c6f6d5;
  color: #2f855a;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #c53030;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const AdminSettings: React.FC = () => {
  const [generalSettings, setGeneralSettings] = useState({
    siteName: 'Spin To Connect',
    siteDescription: 'Find your perfect match with our spinning wheel!',
    contactEmail: 'support@wheelofmatches.com',
    maxWheelParticipants: 20,
    defaultMatchDuration: 7
  });
  
  const [emailSettings, setEmailSettings] = useState({
    smtpServer: 'smtp.example.com',
    smtpPort: 587,
    smtpUsername: 'notifications@wheelofmatches.com',
    smtpPassword: '',
    senderName: 'Spin To Connect',
    senderEmail: 'notifications@wheelofmatches.com',
    enableEmailNotifications: true
  });
  
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleGeneralSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement API call to save general settings
    console.log('Saving general settings:', generalSettings);
    setSuccessMessage('General settings saved successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement API call to save email settings
    console.log('Saving email settings:', emailSettings);
    setSuccessMessage('Email settings saved successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  return (
    <Container>
      <Title>Admin Settings</Title>
      
      <Card>
        <CardTitle>General Settings</CardTitle>
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        
        <Form onSubmit={handleGeneralSubmit}>
          <FormGroup>
            <Label htmlFor="siteName">Site Name</Label>
            <Input
              id="siteName"
              type="text"
              value={generalSettings.siteName}
              onChange={(e) => setGeneralSettings({...generalSettings, siteName: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="siteDescription">Site Description</Label>
            <Input
              id="siteDescription"
              type="text"
              value={generalSettings.siteDescription}
              onChange={(e) => setGeneralSettings({...generalSettings, siteDescription: e.target.value})}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="contactEmail">Contact Email</Label>
            <Input
              id="contactEmail"
              type="email"
              value={generalSettings.contactEmail}
              onChange={(e) => setGeneralSettings({...generalSettings, contactEmail: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="maxWheelParticipants">Maximum Wheel Participants</Label>
            <Input
              id="maxWheelParticipants"
              type="number"
              min="2"
              max="100"
              value={generalSettings.maxWheelParticipants}
              onChange={(e) => setGeneralSettings({...generalSettings, maxWheelParticipants: parseInt(e.target.value)})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="defaultMatchDuration">Default Match Duration (days)</Label>
            <Input
              id="defaultMatchDuration"
              type="number"
              min="1"
              max="30"
              value={generalSettings.defaultMatchDuration}
              onChange={(e) => setGeneralSettings({...generalSettings, defaultMatchDuration: parseInt(e.target.value)})}
              required
            />
          </FormGroup>
          
          <Button type="submit">Save General Settings</Button>
        </Form>
      </Card>
      
      <Card>
        <CardTitle>Email Settings</CardTitle>
        
        <Form onSubmit={handleEmailSubmit}>
          <FormGroup>
            <Label htmlFor="smtpServer">SMTP Server</Label>
            <Input
              id="smtpServer"
              type="text"
              value={emailSettings.smtpServer}
              onChange={(e) => setEmailSettings({...emailSettings, smtpServer: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="smtpPort">SMTP Port</Label>
            <Input
              id="smtpPort"
              type="number"
              value={emailSettings.smtpPort}
              onChange={(e) => setEmailSettings({...emailSettings, smtpPort: parseInt(e.target.value)})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="smtpUsername">SMTP Username</Label>
            <Input
              id="smtpUsername"
              type="text"
              value={emailSettings.smtpUsername}
              onChange={(e) => setEmailSettings({...emailSettings, smtpUsername: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="smtpPassword">SMTP Password</Label>
            <Input
              id="smtpPassword"
              type="password"
              value={emailSettings.smtpPassword}
              onChange={(e) => setEmailSettings({...emailSettings, smtpPassword: e.target.value})}
              placeholder="Leave empty to keep current password"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="senderName">Sender Name</Label>
            <Input
              id="senderName"
              type="text"
              value={emailSettings.senderName}
              onChange={(e) => setEmailSettings({...emailSettings, senderName: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="senderEmail">Sender Email</Label>
            <Input
              id="senderEmail"
              type="email"
              value={emailSettings.senderEmail}
              onChange={(e) => setEmailSettings({...emailSettings, senderEmail: e.target.value})}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                checked={emailSettings.enableEmailNotifications}
                onChange={(e) => setEmailSettings({...emailSettings, enableEmailNotifications: e.target.checked})}
              />
              Enable Email Notifications
            </CheckboxLabel>
          </FormGroup>
          
          <Button type="submit">Save Email Settings</Button>
        </Form>
      </Card>
    </Container>
  );
};

export default AdminSettings; 